import { comfortableRangeFraction } from './constants.js'
import type { VocalRange } from './types.js'

/**
 * Shift a phrase by whole octaves so its centre lands in the middle 60 % of the singer's
 * calibrated range (§5.8).
 *
 * Whole octaves only: transposing by anything else would change the exercise. A phrase's centre
 * is the midpoint of its own lowest and highest note, which is what "where the phrase sits"
 * means to a singer looking at it.
 */
export const transposeIntoRange = (reference: number[], range: VocalRange): number[] => {
  if (reference.length === 0) {
    return reference
  }

  const margin = ((range.high - range.low) * (1 - comfortableRangeFraction)) / 2
  const bandLow = range.low + margin
  const bandHigh = range.high - margin
  const bandCentre = (bandLow + bandHigh) / 2

  const centre = (Math.min(...reference) + Math.max(...reference)) / 2

  /** How far outside the comfortable band the phrase's centre would sit after `octaves`. */
  const outside = (octaves: number) => {
    const shifted = centre + octaves * 12
    if (shifted < bandLow) {
      return bandLow - shifted
    }
    return shifted > bandHigh ? shifted - bandHigh : 0
  }

  /** The real-valued shift landing the centre on the band centre, rounded both ways. */
  const ideal = (bandCentre - centre) / 12
  const candidates = [Math.floor(ideal), Math.ceil(ideal)]

  const best = candidates.reduce((chosen, candidate) => {
    const chosenOutside = outside(chosen)
    const candidateOutside = outside(candidate)
    if (candidateOutside !== chosenOutside) {
      return candidateOutside < chosenOutside ? candidate : chosen
    }
    /** Equally far outside — prefer the one sitting nearer the middle of the band. */
    const chosenDistance = Math.abs(centre + chosen * 12 - bandCentre)
    const candidateDistance = Math.abs(centre + candidate * 12 - bandCentre)
    return candidateDistance < chosenDistance ? candidate : chosen
  })

  return best === 0 ? [...reference] : reference.map(midi => midi + best * 12)
}

export interface RangeFit {
  /** The phrase shifted by whole octaves */
  midis: number[]
  /** Every note lies inside `[range.low, range.high]`. False: the caller should refuse or split the phrase. */
  fits: boolean
  /** How many notes of `midis` lie outside the range; 0 when it fits */
  notesOutside: number
  /** The whole shift applied, in semitones: a multiple of 12 unless the key changed */
  semitones: number
  /** −6 to +6: the key change within `semitones`, or 0 for octaves only */
  keyChange: number
}

export interface FitIntoRangeOptions {
  /**
   * When no octave fits, try a key change: the smallest that fits every note, ties going down (Bowie §2,
   * Pitch Trainer Phase 2). Off by default, so Melody Trainer and Phase 1 keep octave-only fitting.
   */
  keyChange?: boolean
}

/** Key changes in the order to try: the smallest first, and down before up */
const keyChanges = [-1, 1, -2, 2, -3, 3, -4, 4, -5, 5, -6, 6]

/**
 * Like `transposeIntoRange`, but for phrases that may be wider than an exercise phrase, such as whole melodies
 * (GH #2353, Bowie 2026-10-18):
 * 1. the centred shift is kept when every note lands inside the range;
 * 2. otherwise any whole-octave shift that puts every note inside wins, the one centred nearest the range;
 * 3. with `keyChange`, when no octave does: the smallest key change that puts every note inside, ties going down
 *    (Bowie §2, Pitch Trainer Phase 2);
 * 4. when none does (the phrase is wider than the range, or no shift lands it inside), `fits` is false and the
 *    phrase is placed by its centre, so the caller decides rather than presenting it as fitting.
 */
export const fitIntoRange = (reference: number[], range: VocalRange, options: FitIntoRangeOptions = {}): RangeFit => {
  if (reference.length === 0) {
    return { midis: [], fits: true, notesOutside: 0, semitones: 0, keyChange: 0 }
  }
  const lowest = Math.min(...reference)
  const highest = Math.max(...reference)
  const fitsAt = (octaves: number) => lowest + octaves * 12 >= range.low && highest + octaves * 12 <= range.high
  const isOutside = (midi: number) => midi < range.low || midi > range.high

  const shifted = (semitones: number, keyChange = 0): RangeFit => ({
    midis: reference.map(midi => midi + semitones),
    fits: true,
    notesOutside: 0,
    semitones,
    keyChange,
  })

  const centred = transposeIntoRange(reference, range)
  const centredOctaves = (centred[0] - reference[0]) / 12
  if (fitsAt(centredOctaves)) {
    return shifted(centredOctaves * 12)
  }

  /** The whole-octave shifts that keep every note inside, nearest the middle of the range first */
  const fewest = Math.ceil((range.low - lowest) / 12)
  const most = Math.floor((range.high - highest) / 12)
  const rangeCentre = (range.low + range.high) / 2
  const phraseCentre = (lowest + highest) / 2
  const fitting = Array.from({ length: Math.max(0, most - fewest + 1) }, (_, i) => fewest + i).sort(
    (a, b) => Math.abs(phraseCentre + a * 12 - rangeCentre) - Math.abs(phraseCentre + b * 12 - rangeCentre)
  )
  const [octaves] = fitting
  if (octaves !== undefined) {
    return shifted(octaves * 12)
  }

  if (options.keyChange) {
    /**
     * Every shift that keeps the whole phrase inside the range. No octave fits, so they span fewer than
     * 12 semitones: each key change has at most one.
     */
    const lowestShift = Math.ceil(range.low - lowest)
    const highestShift = Math.floor(range.high - highest)
    const shiftFor = (keyChange: number) =>
      Array.from({ length: Math.max(0, highestShift - lowestShift + 1) }, (_, i) => lowestShift + i).find(
        semitones => (((semitones - keyChange) % 12) + 12) % 12 === 0
      )
    for (const keyChange of keyChanges) {
      const semitones = shiftFor(keyChange)
      if (semitones !== undefined) {
        return shifted(semitones, keyChange)
      }
    }
  }

  const centredShift = centredOctaves * 12
  return {
    midis: centred,
    fits: false,
    notesOutside: centred.filter(isOutside).length,
    semitones: centredShift,
    keyChange: 0,
  }
}
